// @ts-check
import React from 'react';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';

import BusComponent from '../BusComponent';
import Tooltip from '../Tooltip';
import Mixpanel, { MIXPANEL_EVENTS, MIXPANEL_LOCATIONS } from '../../helpers/Mixpanel';
/**
 * @typedef Props
 * @property {string} mapInstanceId
 * @property {import('../../types').FeaturedSurveyReport | import('../../types').FeaturedOlapReport} report
 * @property {boolean} isUserStarterTier
 * @property {object} customizationDetailsForStorageReport
 * @property {import('react-intl').intlShape} intl
 * *
 * @typedef State
 * @property {import('../../objects/ReportLimits').default | null} reportLimits
 * @property {boolean} downloadInProgress
 * @property {boolean} downloaded
 * @property {string} reportId
 *
 * @extends {BusComponent<Props, State>}
 */
class LocationAnalysisReportButton extends BusComponent {
    constructor(props, context) {
        super(props, context);
        this.state = {
            reportLimits: null,
            downloadInProgress: false,
            downloaded: false,
            reportId:
                this.props.report.source === 'olap'
                    ? this.props.report.id
                    : this.props.report.surveyCode,
        };
        this.bindGluBusEvents({
            REPORT_LIMITS_GET_SUCCESS: this.onReportLimitsResponse,
            RESET_DOWNLOAD_REPORT_STATE: this.resetDownloadState,
            DOWNLOAD_SERVER_EXPORT_SUCCESS: this.onServerExportSuccess,
            DOWNLOAD_SERVER_EXPORT_ERROR: this.onServerExportError,
        });
    }

    componentDidMount() {
        this.bus.emit('REPORT_LIMITS_GET_REQUEST');
    }

    componentWillUnmount() {
        this.unbindGluBusEvents();
    }

    onReportLimitsResponse = reportLimits => {
        this.setState({
            reportLimits,
        });
    };

    resetDownloadState = () => {
        this.setState({
            downloaded: false,
            downloadInProgress: false,
        });
    };

    emitCreateReportMixpanel = (reportName, lat, lng) => {
        Mixpanel.emitUserEvent(MIXPANEL_EVENTS.CREATE_REPORT, {
            Location: MIXPANEL_LOCATIONS.topNav,
            'Report Name': reportName,
            point: {
                latitude: lat,
                longitude: lng,
            },
            fileExtension: 'xlsx',
        });
    };

    onServerExportSuccess = ({ reportId, reportName, lat, lng }) => {
        if (reportId !== this.state.reportId) return;
        this.setState({ downloaded: true, downloadInProgress: false });
        this.emitCreateReportMixpanel(reportName, lat, lng);
    };

    onServerExportError = ({ reportId, errorMessage }) => {
        if (reportId !== this.state.reportId) return;
        if (errorMessage) {
            console.warn(errorMessage);
        }
        this.setState({ downloadInProgress: false });
    };

    onDownloadReport = () => {
        const { reportId } = this.state;
        this.setState({ downloadInProgress: true });

        const { report, mapInstanceId } = this.props;
        this.emit('DOWNLOAD_SERVER_EXPORT_REQUEST', {
            report,
            mapInstanceId,
            reportId,
        });
    };

    render() {
        const { report, isUserStarterTier, customizationDetailsForStorageReport, facilityCount } =
            this.props;
        const { downloadInProgress, downloaded } = this.state;

        const { reportLimits } = this.state;
        const isDisabled =
            (isUserStarterTier && report.disabledForStarterTier) ||
            (reportLimits && reportLimits.remaining <= 0) ||
            downloaded;

        const reportName =
            customizationDetailsForStorageReport.criterias &&
            customizationDetailsForStorageReport.criterias.length > 0 &&
            report.name === 'Storage Facilities'
                ? report.name + ` (${facilityCount}: filters applied)`
                : report.name;

        const disableStorageFacilitiesFilteredReport =
            reportName.includes('filters applied') && facilityCount === 0;
        const dataTestId = report.id ? `map-popup-report-${report.id}` : `map-popup-report-${report.surveyCode}`;

        return (
            <Tooltip
                placement="bottom"
                mouseEnterDelay={0.5}
                mouseLeaveDelay={0}
                overlay={
                    isUserStarterTier && report.disabledForStarterTier
                        ? this.props.intl.formatMessage({
                              id: 'dataBrowser.upgradeToProForAccess',
                          })
                        : null
                }
            >
                <div className="report-popup__download-btn" key={reportName}>
                    <button
                        onClick={this.onDownloadReport}
                        disabled={
                            isDisabled ||
                            downloadInProgress ||
                            disableStorageFacilitiesFilteredReport
                        }
                        data-testid={dataTestId}
                    >
                        <span
                            className={
                                isDisabled || disableStorageFacilitiesFilteredReport
                                    ? 'disabled'
                                    : ''
                            }
                        >
                            {reportName}
                        </span>
                        <i
                            className={`material-symbols-filled ${downloadInProgress ? 'spinning' : ''}`}
                        >
                            {downloaded
                                ? 'check_circle'
                                : downloadInProgress
                                  ? 'progress_activity'
                                  : 'download'}
                        </i>
                    </button>
                </div>
            </Tooltip>
        );
    }
}

LocationAnalysisReportButton.propTypes = {
    mapInstanceId: PropTypes.string.isRequired,
    report: PropTypes.object.isRequired,
    isUserStarterTier: PropTypes.bool.isRequired,
    customizationDetailsForStorageReport: PropTypes.object.isRequired,
    intl: PropTypes.object.isRequired,
};

export default injectIntl(LocationAnalysisReportButton);
